type ReportSegment = { type: 'prose'; markdown: string } | { type: 'block'; kind: string; markdown: string };

type ReportSectionContent = { title: string; segments: ReportSegment[] };

type SplitReport = {
  /** Everything above the first section, such as the report's own `#` title. */
  intro: string;
  sections: ReportSectionContent[];
};

type Token =
  | { fence: false; line: string }
  | { fence: true; marker: string; info: string; indented: boolean; closed: boolean; lines: string[] };

const OPENING_FENCE = /^( {0,3})(`{3,}|~{3,})(.*)$/;
const CLOSING_FENCE = /^ {0,3}(`{3,}|~{3,})[ \t]*$/;
const SECTION_HEADING = /^ {0,3}##[ \t]+(.+?)(?:[ \t]+#+)?[ \t]*$/;
const SMART_BLOCK = /^footprint:([a-z][\w-]*)$/;
const THEMATIC_BREAK = /^ {0,3}([-*_])(?:[ \t]*\1){2,}[ \t]*$/;
const ESCAPED_PUNCTUATION = /\\([!-/:-@[-`{-~])/g;

// Blank lines only: a first line's indent can be what makes it code rather than prose.
const trimBlankLines = (lines: string[]) =>
  lines
    .join('\n')
    .replace(/^(?:[ \t]*\n)+/, '')
    .trimEnd();

// A card already separates its section, so a rule drawn between sections would only add a line at the card's end.
const dropTrailingRules = (lines: string[]) => {
  let end = lines.length;
  while (end > 0 && (!lines[end - 1].trim() || THEMATIC_BREAK.test(lines[end - 1]))) end -= 1;
  return lines.slice(0, end);
};

const closes = (line: string, marker: string) => {
  const match = CLOSING_FENCE.exec(line);
  return !!match && match[1][0] === marker[0] && match[1].length >= marker.length;
};

// A `##` inside a code fence is code, not a section, so lines are grouped into fences first.
const tokenize = (lines: string[]): Token[] => {
  const tokens: Token[] = [];
  let open: Extract<Token, { fence: true }> | null = null;
  for (const line of lines) {
    if (open) {
      open.lines.push(line);
      if (closes(line, open.marker)) {
        open.closed = true;
        tokens.push(open);
        open = null;
      }
      continue;
    }
    const match = OPENING_FENCE.exec(line);
    // Markdown rejects a backtick fence whose info string holds a backtick, so that line is prose.
    if (match && !(match[2][0] === '`' && match[3].includes('`'))) {
      open = {
        fence: true,
        marker: match[2],
        info: match[3].trim(),
        indented: !!match[1],
        closed: false,
        lines: [line],
      };
      continue;
    }
    tokens.push({ fence: false, line });
  }
  if (open) tokens.push(open);
  return tokens;
};

const segmentsOf = (lines: string[]): ReportSegment[] => {
  const segments: ReportSegment[] = [];
  let prose: string[] = [];
  const flush = () => {
    const markdown = trimBlankLines(prose);
    if (markdown) segments.push({ type: 'prose', markdown });
    prose = [];
  };
  for (const token of tokenize(lines)) {
    // Only a closed fence at column zero is split out: an indented one can belong to a list item, which
    // splitting would cut in two.
    const kind = token.fence && token.closed && !token.indented ? SMART_BLOCK.exec(token.info)?.[1] : undefined;
    if (!token.fence) prose.push(token.line);
    else if (!kind) prose.push(...token.lines);
    else {
      flush();
      segments.push({ type: 'block', kind, markdown: token.lines.join('\n') });
    }
  }
  flush();
  return segments;
};

/** The agent builder's outline writes each section as a numbered `##` heading. */
const splitReportSections = (markdown: string): SplitReport => {
  const intro: string[] = [];
  const sections: { title: string; lines: string[] }[] = [];
  for (const token of tokenize(markdown.split(/\r?\n/))) {
    const heading = token.fence ? null : SECTION_HEADING.exec(token.line);
    if (heading) {
      // Saving through the editor escapes punctuation in a heading, and a card title is plain text.
      const title = heading[1].replace(ESCAPED_PUNCTUATION, '$1').replace(/^\d+\.\s+/, '');
      sections.push({ title, lines: [] });
      continue;
    }
    (sections.at(-1)?.lines ?? intro).push(...(token.fence ? token.lines : [token.line]));
  }
  return {
    intro: trimBlankLines(dropTrailingRules(intro)),
    sections: sections.map(({ title, lines }) => ({ title, segments: segmentsOf(dropTrailingRules(lines)) })),
  };
};

export type { ReportSegment, SplitReport };
export default splitReportSections;
